import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Home,
  Plus,
  GripVertical,
  Settings,
  MoreVertical,
  Trash2,
  Pencil,
  LogOut,
  Palette,
  Plug2,
  CreditCard,
  User,
  ChevronRight,
  ChevronDown,
  ChevronsUpDown,
  Check,
  Zap,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Layers,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { pageApi, Page } from "../services/page.service";
import { workspaceApi } from "../services/workspace.service";
import { useWorkspaceStore } from "../stores/workspace.store";
import { useAuthStore } from "../stores/auth.store";
import { ThemeSettingsModal } from "./ThemeSettingsModal";
import CreatePageModal from "./CreatePageModal";
import { FilterGroupsManagerModal } from "./FilterGroupsManagerModal";
import { useWorkspaceFilterGroupsStore } from "../stores/workspace-filter-groups.store";
import { workspaceFilterGroupsApi } from "../services/workspace-filter-groups.service";
import { IconRenderer } from "../lib/icon-map";
import { useThemePreset } from "../hooks/use-theme-preset";
import { tenantConfig } from "../lib/tenant";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "./ui/dropdown-menu";
import {
  Sidebar as SidebarContainer,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuAction,
} from "./ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { useToast } from "../hooks/use-toast";
import { useWorkspaceRole } from "../hooks/use-workspace-role";

// ─── Plan badge ───────────────────────────────────────────────────────────────

const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  premium: 'Premium',
  enterprise: 'Enterprise',
};

const PLAN_STYLES: Record<string, string> = {
  free: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  pro: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  premium: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  enterprise: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

function PlanBadge({ plan, className = '' }: { plan: string; className?: string }) {
  const label = PLAN_LABELS[plan] ?? plan;
  const style = PLAN_STYLES[plan] ?? PLAN_STYLES.free;
  const isPaid = plan !== 'free';
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none ${style} ${className}`}>
      {isPaid && <Zap className="h-2.5 w-2.5" />}
      {label}
    </span>
  );
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentWorkspace, setCurrentWorkspace, workspaces, setWorkspaces } = useWorkspaceStore();
  const { user, logout } = useAuthStore();
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const { canManageStructure } = useWorkspaceRole();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [localPages, setLocalPages] = useState<any[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isPagesExpanded, setIsPagesExpanded] = useState(true);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [isValidDrop, setIsValidDrop] = useState(false);
  const [originalPages, setOriginalPages] = useState<any[]>([]);
  const [expandedPages, setExpandedPages] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilterGroupsModal, setShowFilterGroupsModal] = useState(false);
  const [showNewWorkspaceDialog, setShowNewWorkspaceDialog] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const pagesContainerRef = useRef<HTMLDivElement>(null);
  const themePreset = useThemePreset();
  const isPiano = themePreset === "piano";
  const isDashboard = themePreset === "dashboard";
  const isCtrl = themePreset === "ctrl";

  const { data: allWorkspaces } = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => workspaceApi.getAll(),
  });

  const { data: workspaceDetails } = useQuery({
    queryKey: ["workspace", currentWorkspace?.id],
    queryFn: () => workspaceApi.getOne(currentWorkspace!.id),
    enabled: !!currentWorkspace,
  });

  const currentPlan: string = (workspaceDetails as any)?.plan ?? 'free';

  useEffect(() => {
    if (allWorkspaces) setWorkspaces(allWorkspaces);
  }, [allWorkspaces, setWorkspaces]);

  const setFilterGroups = useWorkspaceFilterGroupsStore((s) => s.setFilterGroups);
  const { data: filterGroupsData } = useQuery({
    queryKey: ["filter-groups", currentWorkspace?.id],
    queryFn: () => workspaceFilterGroupsApi.getAll(currentWorkspace!.id),
    enabled: !!currentWorkspace,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (filterGroupsData) setFilterGroups(filterGroupsData);
  }, [filterGroupsData, setFilterGroups]);

  const { data: pages } = useQuery({
    queryKey: ["pages", currentWorkspace?.id],
    queryFn: () => pageApi.getAll(currentWorkspace!.id),
    enabled: !!currentWorkspace,
  });

  useEffect(() => {
    if (pages) {
      setLocalPages([...pages]);
    }
  }, [pages]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isReorderMode &&
        pagesContainerRef.current &&
        !pagesContainerRef.current.contains(event.target as Node)
      ) {
        setIsReorderMode(false);
      }
    };

    if (isReorderMode) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isReorderMode]);

  const reorderMutation = useMutation({
    mutationFn: async (reorderedPages: any[]) => {
      // Atualizar a ordem de cada página
      const promises = reorderedPages.map((page, index) =>
        pageApi.reorder(currentWorkspace!.id, page.id, index),
      );
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pages", currentWorkspace?.id],
      });
    },
  });

  const createWorkspaceMutation = useMutation({
    mutationFn: (name: string) => workspaceApi.create({ name }),
    onSuccess: (created) => {
      setWorkspaces([...(workspaces.length > 0 ? workspaces : allWorkspaces ?? []), created]);
      setCurrentWorkspace(created);
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      setShowNewWorkspaceDialog(false);
      setNewWorkspaceName("");
      toast({ title: "Workspace criado com sucesso!" });
    },
    onError: (err: any) => {
      toast({ title: "Erro ao criar workspace", description: err.message, variant: "destructive" });
    },
  });

  const deletePageMutation = useMutation({
    mutationFn: async (slug: string) => {
      return pageApi.delete(currentWorkspace!.id, slug);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["pages", currentWorkspace?.id],
      });
    },
  });

  const toggleLockMutation = useMutation({
    mutationFn: async ({ slug, isLocked }: { slug: string; isLocked: boolean }) => {
      return pageApi.toggleLock(currentWorkspace!.id, slug, isLocked);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pages", currentWorkspace?.id] });
    },
  });

  const toggleHiddenMutation = useMutation({
    mutationFn: async ({ slug, isHidden }: { slug: string; isHidden: boolean }) => {
      return pageApi.toggleHidden(currentWorkspace!.id, slug, isHidden);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pages", currentWorkspace?.id] });
    },
  });

  const updatePageParentMutation = useMutation({
    mutationFn: async ({
      slug,
      parentId,
    }: {
      slug: string;
      parentId: string | null;
    }) => {
      return pageApi.update(currentWorkspace!.id, slug, { parentId });
    },
    onMutate: async ({ slug, parentId }) => {
      // Cancelar queries em andamento
      await queryClient.cancelQueries({
        queryKey: ["pages", currentWorkspace?.id],
      });

      // Snapshot do estado anterior
      const previousPages: Page[] =
        queryClient.getQueryData(["pages", currentWorkspace?.id]) || [];

      // Atualizar otimisticamente
      setLocalPages((currentPages) => {
        const updatedPages = currentPages
          .map((page) => {
            if (page.slug === slug) {
              return { ...page, parentId };
            }
            // Se esta página deve receber a página como filho
            if (page.id === parentId) {
              const updatedPage = { ...page };
              if (!updatedPage.children) updatedPage.children = [];
              // Encontrar a página que será movida
              const pageToMove = currentPages.find((p) => p.slug === slug);
              if (pageToMove) {
                // Remover se já existe
                updatedPage.children = updatedPage.children.filter(
                  (child: Page) => child.id !== pageToMove.id,
                );
                // Adicionar como filho
                updatedPage.children.push({ ...pageToMove, parentId });
              }
              return updatedPage;
            }
            return page;
          })
          .filter((page) => page.slug !== slug || parentId === null); // Remover da lista principal se agora é filho

        return updatedPages;
      });

      // Expandir o pai se necessário
      if (parentId) {
        setExpandedPages((prev) => new Set([...prev, parentId]));
      }

      return { previousPages };
    },
    onError: (_error, _variables, context) => {
      // Reverter em caso de erro
      if (context?.previousPages) {
        queryClient.setQueryData(
          ["pages", currentWorkspace?.id],
          context.previousPages,
        );
        setLocalPages(context.previousPages);
      }
    },
    onSuccess: (_data, _variables) => {
      // Invalidar para garantir sincronização
      queryClient.invalidateQueries({
        queryKey: ["pages", currentWorkspace?.id],
      });
    },
  });

  const handleDeletePage = (
    e: React.MouseEvent,
    slug: string,
    pageName: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (
      window.confirm(`Tem certeza que deseja excluir a página "${pageName}"?`)
    ) {
      // Se a página excluída é a atual, redirecionar para home
      if (location.pathname === `/pages/${slug}`) {
        navigate("/");
      }
      deletePageMutation.mutate(slug);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
    setIsValidDrop(false);
    setOriginalPages([...localPages]);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newPages = [...localPages];
    const draggedPage = newPages[draggedIndex];
    newPages.splice(draggedIndex, 1);
    newPages.splice(index, 0, draggedPage);

    setLocalPages(newPages);
    setDraggedIndex(index);
    setIsValidDrop(true);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null) {
      if (isValidDrop) {
        reorderMutation.mutate(localPages);
      } else {
        // Cancelar: reverter para original
        setLocalPages(originalPages);
      }
    }
    setDraggedIndex(null);
    setIsValidDrop(false);
  };

  const isActive = (path: string) => location.pathname === path;

  const handleGenerateWithAI = async (prompt: string) => {
    if (!currentWorkspace?.id) return;

    try {
      const result = await pageApi.generateWithAI(currentWorkspace.id, prompt);
      
      toast({
        title: "Páginas geradas com sucesso! ✨",
        description: result.explanation,
      });

      // Recarregar as páginas
      queryClient.invalidateQueries({
        queryKey: ["pages", currentWorkspace.id],
      });
    } catch (error: any) {
      console.error("Erro ao gerar páginas:", error);
      
      let errorMessage = "Ocorreu um erro ao gerar as páginas com IA";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      console.log("Detalhes do erro:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      
      toast({
        title: "Erro ao gerar páginas",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const togglePageExpanded = (pageId: string) => {
    setExpandedPages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pageId)) {
        newSet.delete(pageId);
      } else {
        newSet.add(pageId);
      }
      return newSet;
    });
  };

  const renderPageItem = (page: any, level: number = 0) => {
    const hasChildren =
      page.children && Array.isArray(page.children) && page.children.length > 0;
    const isExpanded = expandedPages.has(page.id);
    const isPageHidden = !!(page as any)?.permissions?.isHidden;

    return (
      <div key={page.id} className={isPageHidden ? "opacity-50" : ""}>
        <SidebarMenuItem
          draggable={isReorderMode && level === 0}
          onDragStart={() =>
            isReorderMode &&
            level === 0 &&
            handleDragStart(localPages.findIndex((p) => p.id === page.id))
          }
          onDragOver={(e) =>
            isReorderMode &&
            level === 0 &&
            handleDragOver(
              e,
              localPages.findIndex((p) => p.id === page.id),
            )
          }
          onDragEnd={() => isReorderMode && level === 0 && handleDragEnd()}
          className={`group/item ${isReorderMode && level === 0 && draggedIndex === localPages.findIndex((p) => p.id === page.id) ? "opacity-50" : ""}`}
        >
          <SidebarMenuButton asChild isActive={isActive(`/pages/${page.slug}`)}>
            <Link
              to={`/pages/${page.slug}`}
              className="flex items-center w-full"
            >
              {isReorderMode && level === 0 && canManageStructure && (
                <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab active:cursor-grabbing flex-shrink-0" />
              )}
              {hasChildren ? (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    togglePageExpanded(page.id);
                  }}
                  className="h-4 w-4 flex items-center justify-center flex-shrink-0 mr-1 z-10 relative hover:bg-accent rounded"
                  title={
                    isExpanded ? "Recolher subpáginas" : "Expandir subpáginas"
                  }
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </button>
              ) : level > 0 ? (
                <div className="w-4 flex-shrink-0 mr-1" />
              ) : null}
              <IconRenderer
                icon={page.icon || "📄"}
                className="h-4 w-4 flex-shrink-0"
              />
              <span className="flex-1 truncate ml-2">{page.name}</span>
              {isPageHidden && isSuperAdmin && (
                <EyeOff className="h-3 w-3 ml-1 flex-shrink-0 text-muted-foreground" aria-label="Página oculta" />
              )}
            </Link>
          </SidebarMenuButton>
          {canManageStructure && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuAction
                className={`transition-opacity ${
                  isActive(`/pages/${page.slug}`)
                    ? "opacity-100"
                    : "opacity-0 group-hover/item:opacity-100"
                }`}
                title="Opções"
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </SidebarMenuAction>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="right"
              align="center"
              className="w-48 bg-sidebar"
            >
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/pages/${page.slug}/edit`);
                }}
              >
                <Pencil className="mr-2 h-4 w-4 text-muted-foreground" />
                Editar página
              </DropdownMenuItem>
              {page.parentId ? (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    updatePageParentMutation.mutate({
                      slug: page.slug,
                      parentId: null,
                    });
                  }}
                >
                  <Home className="mr-2 h-4 w-4 text-muted-foreground" />
                  Remover da subpágina
                </DropdownMenuItem>
              ) : (
                !page.children?.length &&
                localPages.filter(
                  (p) =>
                    p.id !== page.id &&
                    (!p.children || p.children.length === 0),
                ).length > 0 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <ChevronRight className="mr-2 h-4 w-4 text-muted-foreground" />
                      Tornar subpágina
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-48 bg-sidebar">
                      {localPages
                        .filter((p) => p.id !== page.id)
                        .map((parentPage) => (
                          <DropdownMenuItem
                            key={parentPage.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              updatePageParentMutation.mutate({
                                slug: page.slug,
                                parentId: parentPage.id,
                              });
                            }}
                            onSelect={() => {
                              updatePageParentMutation.mutate({
                                slug: page.slug,
                                parentId: parentPage.id,
                              });
                            }}
                          >
                            <IconRenderer
                              icon={parentPage.icon || "📄"}
                              className="mr-2 h-4 w-4"
                            />
                            {parentPage.name}
                          </DropdownMenuItem>
                        ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )
              )}
              {isSuperAdmin && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    const newLocked = !(page as any)?.permissions?.isLocked;
                    toggleLockMutation.mutate({ slug: page.slug, isLocked: newLocked });
                  }}
                >
                  {(page as any)?.permissions?.isLocked ? (
                    <><Unlock className="mr-2 h-4 w-4 text-muted-foreground" />Desbloquear página</>
                  ) : (
                    <><Lock className="mr-2 h-4 w-4 text-muted-foreground" />Bloquear página</>
                  )}
                </DropdownMenuItem>
              )}
              {isSuperAdmin && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    const newHidden = !(page as any)?.permissions?.isHidden;
                    toggleHiddenMutation.mutate({ slug: page.slug, isHidden: newHidden });
                  }}
                >
                  {(page as any)?.permissions?.isHidden ? (
                    <><Eye className="mr-2 h-4 w-4 text-muted-foreground" />Mostrar página</>
                  ) : (
                    <><EyeOff className="mr-2 h-4 w-4 text-muted-foreground" />Ocultar página</>
                  )}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePage(e as any, page.slug, page.name);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4 text-muted-foreground" />
                Excluir página
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </SidebarMenuItem>
        {hasChildren && isExpanded && (
          <div className="ml-4 border-l border-border/50 pl-2">
            {page.children.map((child: Page) =>
              renderPageItem(child, level + 1),
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <SidebarContainer>
      <SidebarHeader>
        {isPiano && (
          <div className="piano-sidebar-brand">
            <div className="piano-sidebar-brand__name">
              <span className="piano-sidebar-brand__underscore">_</span>
              {tenantConfig.appName}
            </div>
            <div className="piano-sidebar-brand__sub">ADMIN CONSOLE</div>
          </div>
        )}
        {isDashboard && (
          <div className="dashboard-sidebar-brand">
            <div className="dashboard-sidebar-brand__icon">{tenantConfig.appName.charAt(0)}</div>
            <span className="dashboard-sidebar-brand__text">{tenantConfig.appName}</span>
            <span className="dashboard-sidebar-brand__badge">PRO</span>
          </div>
        )}
        {isCtrl && (
          <div className="ctrl-sidebar-brand">
            <div className="ctrl-sidebar-brand__icon">{tenantConfig.appName.charAt(0)}</div>
            <span className="ctrl-sidebar-brand__text">{tenantConfig.appName.toUpperCase()}</span>
            <span className="ctrl-sidebar-brand__badge">v2</span>
          </div>
        )}
        {(
          /* ── Dropdown de workspace (disponível também em whitelabel) ── */
          <div className="flex items-center justify-between p-2 gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  title="Trocar workspace"
                >
                  <ChevronsUpDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(() => {
                  const list: any[] = workspaces.length > 0 ? workspaces : allWorkspaces ?? [];
                  const own = list.filter((ws: any) => !ws.isSuperAdminAccess);
                  const others = list.filter((ws: any) => ws.isSuperAdminAccess);
                  const renderItem = (ws: any) => (
                    <DropdownMenuItem
                      key={ws.id}
                      onClick={() => setCurrentWorkspace(ws)}
                      className="flex items-center gap-2"
                    >
                      <Check
                        className={`h-4 w-4 shrink-0 ${
                          ws.id === currentWorkspace?.id ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                      <span className="flex-1 truncate">{ws.name}</span>
                      {ws.plan && ws.plan !== 'free' && (
                        <PlanBadge plan={ws.plan} />
                      )}
                    </DropdownMenuItem>
                  );
                  return (
                    <>
                      {own.map(renderItem)}
                      {others.length > 0 && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground py-1">
                            Todos os clientes
                          </DropdownMenuLabel>
                          {(() => {
                            const grouped = new Map<string, { label: string; items: any[] }>();
                            for (const ws of others) {
                              const key = ws.owner?.id ?? 'unknown';
                              const label = ws.owner?.name ?? ws.owner?.email ?? 'Sem dono';
                              if (!grouped.has(key)) grouped.set(key, { label, items: [] });
                              grouped.get(key)!.items.push(ws);
                            }
                            return Array.from(grouped.values()).map((group) => (
                              <div key={group.label}>
                                <p className="px-2 py-1 text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider">
                                  {group.label}
                                </p>
                                {group.items.map(renderItem)}
                              </div>
                            ));
                          })()}
                        </>
                      )}
                    </>
                  );
                })()}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    setNewWorkspaceName("");
                    setShowNewWorkspaceDialog(true);
                  }}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Novo workspace
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold truncate">{currentWorkspace?.name}</h2>
              {tenantConfig.id === 'default' && (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <PlanBadge plan={currentPlan} />
                  {currentPlan === 'free' && (
                    <button
                      onClick={() => navigate('/plans')}
                      className="text-[10px] text-primary hover:underline leading-none"
                    >
                      Fazer upgrade
                    </button>
                  )}
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              title="Configurações do workspace"
              onClick={() => navigate('/workspace/settings')}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/")}>
                  <Link to="/">
                    <Home className="h-4 w-4" />
                    <span>Início</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <Collapsible
          defaultOpen={isPagesExpanded}
          onOpenChange={setIsPagesExpanded}
        >
          <SidebarGroup>
            <div className="flex items-center justify-between px-2 py-1.5">
              <CollapsibleTrigger className="flex-1 flex items-center text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/70 hover:text-sidebar-foreground transition-colors">
                Páginas
              </CollapsibleTrigger>
              <div className="flex items-center gap-0">
                <button
                  className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-accent transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFilterGroupsModal(true);
                  }}
                  title="Grupos de Filtros"
                >
                  <Layers className="h-3 w-3" />
                </button>
                {canManageStructure && (
                  <>
                <button
                  className={`h-6 w-6 flex items-center justify-center rounded-md hover:bg-accent transition-colors ${isReorderMode ? "bg-accent" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsReorderMode(!isReorderMode);
                  }}
                  title={
                    isReorderMode
                      ? "Sair do modo de reordenação"
                      : "Entrar no modo de reordenação"
                  }
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-accent transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCreateModal(true);
                  }}
                  title="Nova página"
                >
                  <Plus className="h-3 w-3" />
                </button>
                  </>
                )}
              </div>
            </div>
            <CollapsibleContent>
              <div ref={pagesContainerRef}>
                <SidebarGroupContent>
                  {localPages && localPages.length > 0 ? (
                    <SidebarMenu>
                      {localPages.map((page: any) => renderPageItem(page))}
                    </SidebarMenu>
                  ) : (
                    <p className="px-4 py-2 text-sm text-muted-foreground">
                      Nenhuma página ainda
                    </p>
                  )}
                </SidebarGroupContent>
              </div>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

      </SidebarContent>
      <SidebarFooter>
        <div className="p-4 border-t">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary overflow-hidden shrink-0">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                user?.name
                  ? user.name.charAt(0).toUpperCase()
                  : user?.email?.charAt(0).toUpperCase() || "U"
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user?.name || "Usuário"}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {user?.email}
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  title="Opções do usuário"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="start"
                className="w-48 bg-sidebar"
              >
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {user?.name || "Usuário"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {user?.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/account')}>
                  <User className="mr-2 h-4 w-4 text-muted-foreground" />
                  Minha conta
                </DropdownMenuItem>
                {tenantConfig.id === 'default' && (
                  <DropdownMenuItem onClick={() => navigate('/integrations')}>
                    <Plug2 className="mr-2 h-4 w-4 text-muted-foreground" />
                    Integrações
                  </DropdownMenuItem>
                )}
                {tenantConfig.id === 'default' && (
                  <DropdownMenuItem onClick={() => navigate('/plans')}>
                    <CreditCard className="mr-2 h-4 w-4 text-muted-foreground" />
                    Planos e preços
                  </DropdownMenuItem>
                )}
                {tenantConfig.id === 'default' && (
                  <ThemeSettingsModal>
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <Palette className="mr-2 h-4 w-4 text-muted-foreground" />
                      Tema
                    </DropdownMenuItem>
                  </ThemeSettingsModal>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    navigate("/auth");
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4 text-muted-foreground" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </SidebarFooter>

      <CreatePageModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onGenerateWithAI={handleGenerateWithAI}
      />

      <FilterGroupsManagerModal
        open={showFilterGroupsModal}
        onClose={() => setShowFilterGroupsModal(false)}
      />

      <Dialog open={showNewWorkspaceDialog} onOpenChange={setShowNewWorkspaceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Workspace</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (newWorkspaceName.trim()) {
                createWorkspaceMutation.mutate(newWorkspaceName.trim());
              }
            }}
          >
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-workspace-name">Nome</Label>
                <Input
                  id="new-workspace-name"
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  placeholder="Nome do workspace"
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewWorkspaceDialog(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!newWorkspaceName.trim() || createWorkspaceMutation.isPending}
              >
                Criar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SidebarContainer>
  );
}
